import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { SUPER_NAV } from "./SuperDashboard";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, X, ChevronRight, BookMarked, Calendar, Layers, Search } from "lucide-react";

/**
 * Super Admin — Book Kit configuration.
 * Manage academic years and per-school kits. Products are referenced from the
 * global catalog (no duplicates). Prices are visible to super admin only.
 */
export default function SuperBookKits() {
  const [kits, setKits] = useState([]);
  const [years, setYears] = useState([]);
  const [schools, setSchools] = useState([]);
  const [q, setQ] = useState("");
  const [filterSchool, setFilterSchool] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [editing, setEditing] = useState(null); // null | {} for new | kit obj
  const [showYearForm, setShowYearForm] = useState(false);

  const load = async () => {
    const [k, y, s] = await Promise.all([
      api.get("/admin/kits", { params: { school_id: filterSchool || undefined, academic_year_id: filterYear || undefined } }),
      api.get("/admin/kits/academic-years"),
      api.get("/admin/schools"),
    ]);
    setKits(k.data || []);
    setYears(y.data || []);
    setSchools(s.data || []);
  };

  useEffect(() => { load().catch((e) => toast.error(formatApiError(e))); }, [filterSchool, filterYear]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return kits;
    return kits.filter((k) => (k.name || "").toLowerCase().includes(ql) ||
                              (k.school_name || "").toLowerCase().includes(ql) ||
                              (k.class_level || "").toLowerCase().includes(ql));
  }, [q, kits]);

  return (
    <DashboardLayout nav={SUPER_NAV} title="Book Kits">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-3 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search kits…"
                 data-testid="input-kit-search"
                 className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:border-brand-blue text-sm"/>
        </div>
        <select data-testid="filter-school" value={filterSchool} onChange={(e) => setFilterSchool(e.target.value)}
                className="rounded-md border border-slate-300 py-2 px-3 text-sm">
          <option value="">All schools</option>
          {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select data-testid="filter-year" value={filterYear} onChange={(e) => setFilterYear(e.target.value)}
                className="rounded-md border border-slate-300 py-2 px-3 text-sm">
          <option value="">All years</option>
          {years.map((y) => <option key={y.id} value={y.id}>{y.name}</option>)}
        </select>
        <button data-testid="btn-new-year" onClick={() => setShowYearForm(true)}
                className="inline-flex items-center gap-1 rounded-md border border-slate-300 py-2 px-3 text-sm hover:bg-slate-50">
          <Calendar size={14}/> Add year
        </button>
        <button data-testid="btn-new-kit" onClick={() => setEditing({})}
                className="ml-auto inline-flex items-center gap-2 px-3 py-2 rounded-md bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blueDark">
          <Plus size={14} /> New Kit
        </button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Kit</th>
              <th className="text-left px-4 py-3">School</th>
              <th className="text-left px-4 py-3">Year</th>
              <th className="text-left px-4 py-3">Class</th>
              <th className="text-left px-4 py-3">Language</th>
              <th className="text-left px-4 py-3">Stream / Combination</th>
              <th className="text-left px-4 py-3">Items</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((k) => (
              <tr key={k.id} data-testid={`kit-row-${k.id}`} className="hover:bg-slate-50/50 cursor-pointer" onClick={() => setEditing(k)}>
                <td className="px-4 py-3 font-semibold text-brand-navy">
                  <div className="flex items-center gap-2"><BookMarked size={14} className="text-brand-blue"/>{k.name}</div>
                  {k.is_demo && <span className="ml-6 text-[10px] font-bold uppercase tracking-wider text-amber-700">DEMO</span>}
                </td>
                <td className="px-4 py-3 text-slate-600">{k.school_name}</td>
                <td className="px-4 py-3 text-slate-600">{k.academic_year_name}</td>
                <td className="px-4 py-3 text-slate-600">{k.class_level}</td>
                <td className="px-4 py-3 text-slate-600">{k.second_language || <span className="text-slate-300">—</span>}</td>
                <td className="px-4 py-3 text-slate-600">{k.subject_combination || <span className="text-slate-300">—</span>}</td>
                <td className="px-4 py-3 text-slate-600">{k.item_count}</td>
                <td className="px-4 py-3">
                  {k.is_published
                    ? <span className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Published</span>
                    : <span className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">Draft</span>}
                </td>
                <td className="px-4 py-3 text-right"><ChevronRight size={14} className="text-slate-400 inline"/></td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-slate-500">
                No kits yet. <button className="text-brand-blue font-semibold ml-2" onClick={() => setEditing({})}>Create the first kit →</button>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <KitEditor kit={editing} schools={schools} years={years}
                    onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
      {showYearForm && (
        <YearForm existing={years}
                   onClose={() => setShowYearForm(false)}
                   onSaved={() => { setShowYearForm(false); load(); }} />
      )}
    </DashboardLayout>
  );
}

function YearForm({ existing, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [current, setCurrent] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api.post("/admin/kits/academic-years", { name: name.trim(), is_current: current });
      toast.success("Academic year saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="year-form">
      <div className="flex-1 bg-black/40" onClick={onClose}/>
      <aside className="w-full sm:w-[380px] h-full bg-white flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="font-heading font-semibold">New Academic Year</div>
          <button onClick={onClose}><X size={16}/></button>
        </div>
        <div className="p-4 space-y-3">
          <label className="block">
            <span className="text-xs font-semibold text-slate-600">Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="e.g. 2026-2027"
                   data-testid="year-name"
                   className="mt-1 w-full rounded-md border border-slate-300 py-2 px-3 text-sm focus:outline-none focus:border-brand-blue"/>
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={current} onChange={(e) => setCurrent(e.target.checked)} data-testid="year-current"/>
            Set as current year
          </label>
          {existing.length > 0 && (
            <div className="text-xs text-slate-500">
              <div className="font-semibold mb-1">Existing:</div>
              <div className="flex flex-wrap gap-1">{existing.map((y) => <span key={y.id} className="px-2 py-0.5 bg-slate-100 rounded">{y.name}</span>)}</div>
            </div>
          )}
        </div>
        <div className="p-4 border-t border-slate-200">
          <button data-testid="btn-save-year" onClick={save} disabled={saving}
                  className="w-full py-2 rounded-md bg-brand-blue text-white font-semibold disabled:bg-slate-300">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </aside>
    </div>
  );
}

function KitEditor({ kit, schools, years, onClose, onSaved }) {
  const isNew = !kit.id;
  const [form, setForm] = useState({
    school_id: kit.school_id || "",
    academic_year_id: kit.academic_year_id || "",
    class_level: kit.class_level || "",
    second_language: kit.second_language || "",
    subject_combination: kit.subject_combination || "",
    name: kit.name || "",
    description: kit.description || "",
    is_published: kit.is_published ?? true,
    is_demo: kit.is_demo ?? false,
  });
  const [items, setItems] = useState((kit.items || []).map((it) => ({
    product_id: it.product_id,
    product_name: it.product?.name || it.product_name || "(product)",
    qty: it.qty,
    required: it.required ?? true,
  })));
  const [productSearch, setProductSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Full kit detail if editing
    if (kit.id && !kit.items) {
      api.get(`/admin/kits/${kit.id}`).then((r) => {
        setItems((r.data.items || []).map((it) => ({
          product_id: it.product_id,
          product_name: it.product?.name,
          qty: it.qty,
          required: it.required,
        })));
      });
    }
  }, [kit.id]);

  useEffect(() => {
    if (!productSearch.trim()) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      const { data } = await api.get("/admin/products", { params: { q: productSearch, active: true } });
      setSearchResults(data || []);
    }, 250);
    return () => clearTimeout(t);
  }, [productSearch]);

  const addItem = (p) => {
    if (items.find((it) => it.product_id === p.id)) return;
    setItems((prev) => [...prev, { product_id: p.id, product_name: p.name, qty: 1, required: true }]);
    setProductSearch("");
    setSearchResults([]);
  };

  const save = async () => {
    if (!form.school_id || !form.academic_year_id || !form.class_level || !form.name || items.length === 0) {
      toast.error("Please fill school, year, class, name and at least one item");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        second_language: form.second_language || null,
        subject_combination: form.subject_combination || null,
        items: items.map((it, idx) => ({
          product_id: it.product_id, qty: Number(it.qty) || 1,
          required: !!it.required, sort_order: idx,
        })),
      };
      if (isNew) {
        await api.post("/admin/kits", payload);
        toast.success("Kit created");
      } else {
        await api.patch(`/admin/kits/${kit.id}`, payload);
        toast.success("Kit updated");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm(`Delete "${form.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/kits/${kit.id}`);
      toast.success("Kit deleted");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="kit-editor">
      <div className="flex-1 bg-black/40" onClick={onClose}/>
      <aside className="w-full sm:w-[540px] h-full bg-white flex flex-col overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="font-heading font-semibold">{isNew ? "New Book Kit" : `Edit — ${kit.name}`}</div>
          <button onClick={onClose}><X size={16}/></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="School *">
              <Select value={form.school_id} onChange={(v) => setForm((f) => ({...f, school_id: v}))} data-testid="kit-school">
                <option value="">Select</option>
                {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Select>
            </Field>
            <Field label="Academic Year *">
              <Select value={form.academic_year_id} onChange={(v) => setForm((f) => ({...f, academic_year_id: v}))} data-testid="kit-year">
                <option value="">Select</option>
                {years.map((y) => <option key={y.id} value={y.id}>{y.name}</option>)}
              </Select>
            </Field>
            <Field label="Class *">
              <input value={form.class_level} onChange={(e) => setForm((f) => ({...f, class_level: e.target.value}))}
                     placeholder="e.g. I, V, IX, XI"
                     data-testid="kit-class"
                     className="w-full rounded-md border border-slate-300 py-2 px-3 text-sm"/>
            </Field>
            <Field label="Second Language (optional)">
              <input value={form.second_language} onChange={(e) => setForm((f) => ({...f, second_language: e.target.value}))}
                     placeholder="Hindi, Bengali, French…"
                     data-testid="kit-lang"
                     className="w-full rounded-md border border-slate-300 py-2 px-3 text-sm"/>
            </Field>
            <Field label="Subject Combination / Stream (optional)" full>
              <input value={form.subject_combination} onChange={(e) => setForm((f) => ({...f, subject_combination: e.target.value}))}
                     placeholder="Science, Commerce, Humanities, or ICSE combination"
                     data-testid="kit-combo"
                     className="w-full rounded-md border border-slate-300 py-2 px-3 text-sm"/>
            </Field>
            <Field label="Kit Name *" full>
              <input value={form.name} onChange={(e) => setForm((f) => ({...f, name: e.target.value}))}
                     data-testid="kit-name"
                     className="w-full rounded-md border border-slate-300 py-2 px-3 text-sm"/>
            </Field>
            <Field label="Description" full>
              <textarea rows={2} value={form.description}
                        onChange={(e) => setForm((f) => ({...f, description: e.target.value}))}
                        data-testid="kit-desc"
                        className="w-full rounded-md border border-slate-300 py-2 px-3 text-sm"/>
            </Field>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={form.is_published}
                     onChange={(e) => setForm((f) => ({...f, is_published: e.target.checked}))} data-testid="kit-pub"/>
              Published (visible to parents)
            </label>
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={form.is_demo}
                     onChange={(e) => setForm((f) => ({...f, is_demo: e.target.checked}))} data-testid="kit-demo"/>
              Demo kit
            </label>
          </div>

          {/* ---------- Items ---------- */}
          <div className="pt-2 border-t border-slate-200">
            <div className="font-semibold text-slate-700 text-sm mb-2 flex items-center gap-2">
              <Layers size={14}/> Items ({items.length})
            </div>
            <div className="relative">
              <input value={productSearch} onChange={(e) => setProductSearch(e.target.value)}
                     placeholder="Add products by name…"
                     data-testid="kit-item-search"
                     className="w-full rounded-md border border-slate-300 py-2 px-3 text-sm"/>
              {searchResults.length > 0 && (
                <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-56 overflow-y-auto">
                  {searchResults.map((p) => (
                    <button key={p.id} onClick={() => addItem(p)}
                            className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm border-b border-slate-100 last:border-0"
                            data-testid={`kit-add-product-${p.id}`}>
                      <div className="font-semibold text-slate-800 truncate">{p.name}</div>
                      <div className="text-xs text-slate-500">{p.category} · ₹{Number(p.price || 0).toLocaleString('en-IN')}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="mt-3 space-y-2">
              {items.map((it, idx) => (
                <div key={it.product_id} className="flex items-center gap-2 border border-slate-200 rounded-md p-2">
                  <div className="flex-1 min-w-0 text-sm text-slate-800 truncate" data-testid={`kit-item-${it.product_id}`}>{it.product_name}</div>
                  <input type="number" min={1} max={50} value={it.qty}
                         onChange={(e) => setItems((prev) => prev.map((x, i) => i === idx ? {...x, qty: Number(e.target.value)} : x))}
                         data-testid={`kit-item-qty-${it.product_id}`}
                         className="w-16 rounded-md border border-slate-300 py-1 px-2 text-sm text-center"/>
                  <label className="text-xs text-slate-600 flex items-center gap-1">
                    <input type="checkbox" checked={it.required}
                           onChange={(e) => setItems((prev) => prev.map((x, i) => i === idx ? {...x, required: e.target.checked} : x))}/>
                    req
                  </label>
                  <button onClick={() => setItems((prev) => prev.filter((_, i) => i !== idx))}
                          data-testid={`kit-item-remove-${it.product_id}`}
                          className="text-rose-500 hover:text-rose-700">
                    <Trash2 size={14}/>
                  </button>
                </div>
              ))}
              {items.length === 0 && <div className="text-sm text-slate-500 text-center py-4">No items yet — search above to add.</div>}
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-slate-200 flex items-center gap-2">
          {!isNew && (
            <button onClick={del} data-testid="btn-delete-kit"
                    className="inline-flex items-center gap-1 py-2 px-3 rounded-md border border-rose-300 text-rose-600 text-sm hover:bg-rose-50">
              <Trash2 size={14}/> Delete
            </button>
          )}
          <button onClick={save} disabled={saving} data-testid="btn-save-kit"
                  className="ml-auto inline-flex items-center gap-2 py-2 px-4 rounded-md bg-brand-blue text-white font-semibold disabled:bg-slate-300">
            <Save size={14}/> {saving ? "Saving…" : "Save Kit"}
          </button>
        </div>
      </aside>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-xs font-semibold text-slate-600 mb-1">{label}</div>
      {children}
    </div>
  );
}
function Select({ value, onChange, children, ...rest }) {
  return <select value={value} onChange={(e) => onChange(e.target.value)}
                 className="w-full rounded-md border border-slate-300 py-2 px-3 text-sm bg-white" {...rest}>{children}</select>;
}
